// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DataTreeActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import AppGlobal from 'Izenda.AppGlobal';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import { withHandlers } from 'recompose';
import { QUERY_SOURCE_TYPE } from 'logic/data/Izenda.QuerySourceTypeConfig';
import { curry } from 'lodash/fp';
const DataTreeActions = withHandlers({
  toggleSourceSelection: curry(toggleSourceSelection, 1),
  deleteCalculatedField: curry(deleteCalculatedField, 1)
});

/**
 * Toggle data source selection
 * @param  {Object} props
 * @param  {Object} dataObject
 * @param  {Boolean} selected
 * @param  {TreeNode} node
 */
function toggleSourceSelection(props: any, dataObject: any, selected: boolean, node: any) {
  const { store, loader } = props;
  const { reportStore, dataTree } = store;
  const fieldStore = reportStore.fieldsStore;
  const { reportFilters: reportFiltersModel, dataTree: fieldStoreDataTree } = fieldStore;
  reportStore.model.markDirty();
  reportStore.alertDetails.reset();

  if (selected) {
    // display error message if selected items exceed limitation
    if (dataTree.selectedItems.length >= AppGlobal.dataSourceLimitValue) {
      props.confirmFn('Information', {
        description: constants.BUNDLE.MESSAGE.M16,
        type: 'info',
        showCloseIndicator: true
      });
    } else {
      loader.startLoading();
      return store
        .startLoadingRelationship()
        .then(store.handleChangeSelectedQuerySource.bind(store, dataObject, true, node))
        .then(() => {
          if (dataObject.isStoreProcedure) {
            store.model.addFilterParams(dataObject);
            return fieldStore.getFilterData(null, null, true).then(() => {
              // add parameters of stored procedures into filter if any
              reportFiltersModel.addFilterByIds(fieldStoreDataTree.parameterFieldIds, {
                isParameter: true
              });
              return Promise.resolve();
            });
          }
          return Promise.resolve();
        })
        .then(store.endLoadingRelationship.bind(store))
        .finally(loader.endLoading.bind(loader));
    }
  } else {
    return store
      .checkRemoveSelectedRelation(dataObject)
      .then(result => confirmUnselectRelation(props, result, node))
      .then(() => {
        // Clear config field when unselect store procedure
        dataObject.isStoreProcedure && fieldStore.reportFilters.setConfigModeToField(null);
      })
      .finally(loader.endLoading.bind(loader));
  }
}

/**
 * Confirm unselect relation
 * @param  {Object} props
 * @param  {Object} result
 * @param  {TreeNode} node
 * @return {Promise}
 */
function confirmUnselectRelation(props: any, result: any, node: any) {
  const { store, loader } = props;
  const dataObject = result.dataObject;
  const reportFilters = store.reportStore.fieldsStore.reportFilters;
  const confirmMessage =
    store.model.reportMode === constants.ADVANCED
      ? constants.BUNDLE.MESSAGE.ADVANCED_UNSELECT
      : constants.BUNDLE.MESSAGE.SIMPLE_UNSELECT;

  function updateUncheckValue() {
    loader.startLoading();
    reportFilters.removeFiltersOfUncheckedDataObject(dataObject);
    return store.handleChangeSelectedQuerySource(dataObject, false, node).then(() => {
      // call updateTree to ensure that displayData nodes have proper selected properties
      store.updateTree();
      return loader.endLoading.bind(loader)
    });
  }

  if (store.model.reportMode !== constants.ADVANCED || result.hasRemovedRelationship) {
    return props
      .confirmFn('Confirmation', {
        description: String.format(confirmMessage, result.dataObject.name),
        isHTMLFormat: true
      })
      .then(updateUncheckValue);
  } else {
    return updateUncheckValue();
  }
}

/**
 * Delete calculated field
 * @param  {Object} props
 * @param  {Object} field
 * @return {Promise}
 */
function deleteCalculatedField(props: any, field: any) {
  const { store: dataSourceStore, localization } = props;
  const reportStore = dataSourceStore.reportStore || {};
  const fieldStore = reportStore.fieldsStore;

  // Check has calculated field in report body
  const hasCalculatedField = fieldStore.reportBody.hasCalculatedField(field.id, field.name);
  // Check filter has use calculated field
  const filterUseCalculated = fieldStore.reportFilters.isFilterUseCalculatedField(field);
  // Check another calculated field use this calculated field
  const anotherCalculatedFieldUse = fieldStore.dataTree.isAnotherCalculatedFieldUse(field);

  if (hasCalculatedField || filterUseCalculated || anotherCalculatedFieldUse) {
    return props.confirmMessage('error', localization.getText('CALCULATED_FIELD_MESSAGE_HAS_BEEN_USED'));
  }

  return fieldStore.deleteCalculatedField(field);
}

export default DataTreeActions;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/DataSourceTree/Izenda.DataSourceTreeActions.js